/** @format */

import svgTools from './svg_tools';

const {
    buildPath,
    buildPathFromObj,
    claimIdOfClosestNode,
    findClosestNode,
    findDiffs,
    invertPath,
    makePathAbsolute,
    objPathToArray,
    pathFromDiff,
    pathToObj,
    profilePath,
    reorderPath,
    repeatFirst,
    reversePath
} = svgTools();

import {SHAPE_CARTON_AUTOLOCK, SHAPE_CARTON_STRAIGHT_TUCK} from 'frontier-constants';
import {
    getBottomTuckKnockoutDuplex,
    getClosureKissLines,
    getGlueFlapKnockout,
    getGlueFlapKnockoutDuplex,
    getGlueKissLines,
    getTuckFlapKnockout
} from 'frontier/components/die_template/kiss_calculations';
import {
    bleedlineStyle,
    creaselineStyle,
    dielineStyle,
    kissStyle,
    knockoutStyle,
    safeZoneStyle
} from 'frontier/components/die_template/line_styles';
import {Colors, DxfWriter, point2d} from '@tarikjabiri/dxf';
import ViewBox from 'frontier/components/die_template/view_box';
import React from 'react';

export default function CartonSvg({
    width,
    length,
    depth,
    flatHeight,
    flatWidth,
    shapeId,
    ...props
}) {
    const bleed = 0.0625;

    const paperThickness = 0.018;
    // https://app.asana.com/0/1201602256487860/1203922303958489/f
    // Makes a few panels slightly smaller
    // Currently applies to the far right panel
    // And the top left closure flap
    // To accomodate automated equipment
    const strippingOffset = 0.03;

    const tuckFlap = 0.5;
    const topBoxOffsetY = tuckFlap + width - paperThickness * 2;
    let glueFlapWidth = 0.5;
    switch (width) {
        case 0.5:
            glueFlapWidth = 0.45;
            break;
        case 0.375:
            glueFlapWidth = 0.325;
            break;
    }
    const glueFlapOffsetX = glueFlapWidth;
    let dustFlapHeight = width * 0.5;

    let topFlapInset = 0.105588;

    const tuckFlapHeight = 0.5; // Flap height from the CREASE line, not the die line
    const tuckFlapInset = 0.02; // Inset for the arcs
    const tuckFlapCutHeight = 0.0787402; // 2mm Cut down in the top flap
    const topFlapWidth = length - 0.6556; // Width of the very top between the arcs
    const tuckFlapFlapCut = (length - topFlapWidth - bleed) / 2; // Top Tuck cut in width
    const bottomTuckHeight = 0.375;
    const centerOffset = -length / 2 + 0.07 + bottomTuckHeight + length * (bottomTuckHeight / 2);

    let notchHeight = dustFlapHeight * 0.04 + 0.2;
    let notchWidth = 0.12;
    let notchCutSpace = 0.14;
    let notchCutGapLeft = notchCutSpace / 2 - strippingOffset;
    let notchCutGapRight = notchCutSpace / 2 + strippingOffset;
    let notchRemainingHeight = -dustFlapHeight + notchHeight + 0.12;

    if (width <= 0.75) {
        dustFlapHeight = Math.max(0.25, width * 0.5);
        notchHeight = 0.125;
        notchWidth = 0.04;
        notchRemainingHeight = -dustFlapHeight + notchHeight + notchWidth;
        // // Limits the top width of the dust flap
        notchCutSpace = 0;
        notchCutGapLeft = 0;
        notchCutGapRight = 0;
    }

    const notchSlopeWidth = (0.43 / 1.61) * notchRemainingHeight * -1;

    const calculateDieLinesObj = (flapCuts = true) => {
        // The little tabs just below the curved top
        const flapCutLeft = ({topFlapWidth}) => {
            return [
                {relative: true, x: tuckFlapFlapCut, y: 0},
                {relative: true, x: 0, y: tuckFlapCutHeight},
                {relative: true, x: 0, y: -tuckFlapCutHeight},
                {relative: true, x: -tuckFlapFlapCut, y: 0}
            ];
        };

        // The little tabs just below the curved top
        const flapCutRight = ({topFlapWidth}) => {
            return [
                {relative: true, x: -tuckFlapFlapCut, y: 0},
                {relative: true, x: 0, y: tuckFlapCutHeight},
                {relative: true, x: 0, y: -tuckFlapCutHeight},
                {relative: true, x: tuckFlapFlapCut + tuckFlapInset, y: 0}
            ];
        };

        // The third panel from the left, with the tw-rounded top
        const topTuckFlap = notchCutSpace => {
            const fourths = (length - bleed) / 4;
            const curveWidth = 0.3088;

            const tuckFlapWithoutCutHeight = tuckFlapHeight - 0.04 + 0.004;
            let coords = [
                {id: 8, relative: true, x: 0, y: notchCutSpace},
                {id: 8.1, relative: true, x: notchCutGapRight, y: 0},
                {id: 9, relative: true, x: 0, y: -dustFlapHeight},
                {id: 10, relative: true, x: 0, y: dustFlapHeight - width},
                {id: 11, relative: true, x: tuckFlapInset, y: 0}
            ];

            if (flapCuts) {
                coords = [...coords, ...flapCutLeft({topFlapWidth})];
            }

            coords = [
                ...coords,
                {
                    id: 12,
                    relative: true,
                    x: curveWidth,
                    y: -tuckFlapWithoutCutHeight,
                    curve: {
                        type: 'quadratic',
                        control_end: {
                            x: 0,
                            y: -tuckFlapWithoutCutHeight * 0.6775
                        }
                    }
                },
                {id: 13, relative: true, x: topFlapWidth, y: 0},
                {
                    id: 14,
                    relative: true,
                    x: curveWidth,
                    y: tuckFlapWithoutCutHeight,
                    curve: {
                        type: 'quadratic',
                        control_end: {
                            x: curveWidth,
                            y: tuckFlapWithoutCutHeight * 0.3225
                        }
                    }
                }
            ];

            if (flapCuts) {
                coords = [...coords, ...flapCutRight({topFlapWidth})];
            } else {
                coords = [...coords, {id: 15, relative: true, x: tuckFlapInset, y: 0}];
            }

            coords = [
                ...coords,
                {id: 16, relative: true, x: 0, y: width - dustFlapHeight},
                {id: 17, relative: true, x: 0, y: dustFlapHeight},
                {id: 17.1, relative: true, x: notchCutGapRight, y: 0},
                {id: 18, relative: true, x: 0, y: -notchCutSpace}
            ];

            return coords;
        };

        // I want closure... so the name is on purpose.
        const closureFlap = (isRight = false) => {
            const rightShift = isRight ? 0.03 : 0;
            // See the getTuckFlapKnockout function for how these are used elsewhere
            const leftDiagonalX = width / 2 + width * 0.318 - width;
            const leftDiagonalY = -width / 2;
            // Line moves from right to left
            return [
                {id: isRight ? 24 : 38, relative: true, x: -width / 2, y: width / 2}, // First diagonal line right to left
                {id: isRight ? 25 : 39, relative: true, x: -width * 0.318 + rightShift, y: 0}, // Flat line at the bottom
                {
                    id: isRight ? 26 : 40,
                    relative: true,
                    x: leftDiagonalX,
                    y: leftDiagonalY
                } // Second diagonal line right to left returns to the bottom of the panel
            ];
        };

        // I want closure... so the name is on purpose.
        // The closure panel are the panels on the bottom. It is the first and third panel from the left.
        // This is laid out from the right to the left. So the first point is far to the right
        const closurePanel = (offsetLeft, isRight = false) => {
            return [
                {
                    id: isRight ? 27 : 41,
                    relative: true,
                    x: -0.02,
                    y: 0,
                    curve: {
                        type: 'quadratic',
                        control_end: {
                            x: -0.01, //-0.02,
                            y: -0.01 //0
                        }
                    }
                }, // tw-rounded corner
                {id: isRight ? 28 : 42, relative: true, x: -width * 0.1, y: width * 0.1}, // id: 42
                {
                    id: isRight ? 29 : 43,
                    x: offsetLeft - 0.04 * width,
                    y: topBoxOffsetY + depth + width / 2
                }, // id: 43
                {
                    id: isRight ? 30 : 44, // id: 44
                    x: offsetLeft - 0.04 * width,
                    y: topBoxOffsetY + depth + width / 2 + bottomTuckHeight
                },
                {
                    id: isRight ? 31 : 45, // id: 45
                    x: offsetLeft - width / 2 + bottomTuckHeight,
                    y: topBoxOffsetY + depth + width / 2 + bottomTuckHeight
                },
                {
                    id: isRight ? 32 : 46,
                    x: offsetLeft - width / 2,
                    y: topBoxOffsetY + depth + width / 2
                }, // id: 46
                {
                    id: isRight ? 33 : 47,
                    x: offsetLeft - length / 2,
                    y: topBoxOffsetY + depth + width / 2
                }, // Middle point id: 47
                {id: isRight ? 34 : 48, relative: true, x: 0, y: bottomTuckHeight / 4}, // id: 48
                {
                    id: isRight ? 35 : 49,
                    relative: true,
                    x: (-bottomTuckHeight / 4) * 3,
                    y: (bottomTuckHeight / 4) * 3
                }, // id: 49
                {
                    id: isRight ? 36 : 50,
                    relative: true,
                    x: -length / 2 + bottomTuckHeight / 2 + 0.21,
                    y: 0
                }, //id: 50
                {id: isRight ? 37 : 51, x: offsetLeft - length, y: topBoxOffsetY + depth} // id: 51
            ];
        };

        let top = [
            {id: 0, x: 0, y: topBoxOffsetY + 0.13},
            {id: 1, x: glueFlapWidth, y: topBoxOffsetY},
            {id: 2, relative: true, x: length + strippingOffset, y: 0},
            {id: 3, relative: true, x: 0, y: -notchHeight},
            {id: 4, relative: true, x: notchWidth, y: notchWidth * -1}, //-0.12
            {id: 5, relative: true, x: notchSlopeWidth, y: notchRemainingHeight},
            {
                id: 6,
                relative: true,
                x: width - strippingOffset - notchWidth - notchSlopeWidth - notchCutSpace,
                y: 0
            },
            {
                id: 7,
                relative: true,
                x: notchCutGapLeft,
                y: dustFlapHeight - notchCutSpace
            },
            ...topTuckFlap(notchCutSpace),
            {
                id: 19,
                relative: true,
                x: notchCutGapLeft,
                y: -dustFlapHeight + notchCutSpace
            },
            {
                id: 20,
                relative: true,
                x: width - strippingOffset - notchWidth - notchSlopeWidth - notchCutSpace,
                y: 0
            },
            {id: 21, relative: true, x: notchSlopeWidth, y: -notchRemainingHeight},
            {
                id: 22,
                x: glueFlapWidth + length * 2 + width * 2 - strippingOffset,
                y: topBoxOffsetY - notchHeight
            },
            {
                id: '22a',
                x: glueFlapWidth + length * 2 + width * 2 - strippingOffset,
                y: topBoxOffsetY + 0.13
            }
        ];
        let bottom = [
            {
                id: 23,
                x: glueFlapWidth + length * 2 + width * 2 - strippingOffset,
                y: topBoxOffsetY + depth
            }, // Bottom right corner
            ...closureFlap(true),
            ...closurePanel(glueFlapWidth + length * 2 + width, true),
            ...closureFlap(false),
            ...closurePanel(glueFlapWidth + length, false),
            {id: 52, x: 0, y: topBoxOffsetY + depth - 0.5}
        ];

        let coords = [...top];

        if (shapeId === SHAPE_CARTON_AUTOLOCK) {
            coords = [...coords, ...bottom];
        }

        return coords;
    };

    const inner = () => {
        let top = [
            {
                id: 0,
                type: 'M',
                relative: false,
                x: 0.0625 + 0.44548881944444446 + 0.0625,
                y: topBoxOffsetY + 0.13 + 0.044326 - 0.115824375 + 0.004
            },
            {
                id: 2,
                type: 'H',
                relative: false,
                x: length + glueFlapWidth + strippingOffset + bleed
            },
            {
                id: 3,
                type: 'v',
                relative: true,
                y: -notchHeight - 0.027 //-0.2766113333333333
            },
            {
                id: 4,
                type: 'l',
                relative: true,
                x: 0.11355930555555556,
                y: -0.11356608333333333
            },
            {
                id: 5,
                type: 'l',
                relative: true,
                x: notchSlopeWidth - 0.00809,
                y: notchRemainingHeight + 0.030177
            },
            {
                id: 6,
                type: 'h',
                relative: true,
                x:
                    width -
                    strippingOffset -
                    notchWidth -
                    notchSlopeWidth -
                    notchCutSpace -
                    topFlapInset //width - notchWidth - notchSlopeWidth - notchCutSpace - 0.105588 - strippingOffset
            },
            {
                id: 7,
                type: 'l',
                relative: true,
                x: notchCutSpace / 2 - 0.003688,
                y: dustFlapHeight - notchCutSpace - 0.04537
            },
            {
                id: 8,
                type: 'l',
                relative: true,
                x: notchCutSpace / 2 + 0.123807,
                y: notchCutSpace + 0.247614
            },
            {
                id: 10,
                type: 'V',
                relative: false,
                y: 0.585001625
            },
            {
                id: 11,
                type: 'h',
                relative: true,
                x: 0.020005972222222224
            },
            {
                type: 'v',
                relative: true,
                y: -0.0625
            },
            {
                id: 12,
                type: 'c',
                relative: true,
                curve: {
                    type: 'cubic',
                    control_start: {
                        x: 0,
                        y: -0.17900933333333333
                    },
                    control_end: {
                        x: 0.08536783333333334,
                        y: -0.30917019444444443
                    }
                },
                x: 0.260837125,
                y: -0.397501625
            },
            {
                id: 13,
                type: 'h',
                relative: true,
                x: topFlapWidth - 0.029079
            },
            {
                id: 14,
                type: 'c',
                relative: true,
                curve: {
                    type: 'cubic',
                    control_start: {
                        x: 0.17546929166666667,
                        y: 0.08833143055555555
                    },
                    control_end: {
                        x: 0.260837125,
                        y: 0.21849229166666667
                    }
                },
                x: 0.260837125,
                y: 0.397501625
            },
            {
                type: 'v',
                relative: true,
                y: 0.0625
            },
            {
                id: 15,
                type: 'h',
                relative: true,
                x: 0.020005972222222224
            },
            {
                // bottom right tip of closure flap
                id: 17,
                type: 'l',
                relative: true,
                x: 0,
                y: width + 0.202349
            },
            {
                id: 18,
                type: 'l',
                relative: true,
                x: notchCutSpace / 2 + 0.123793,
                y: -notchCutSpace - 0.247726
            },
            {
                id: 19,
                type: 'l',
                relative: true,
                x: notchCutSpace / 2 - 0.003688,
                y: -dustFlapHeight + notchCutSpace + 0.045377
            },
            {
                id: 20,
                type: 'h',
                relative: true,
                x: width - notchWidth - notchSlopeWidth - notchCutSpace - 0.105581 - strippingOffset
            },
            {
                id: 21,
                type: 'l',
                relative: true,
                x: notchSlopeWidth - 0.008158,
                y: -notchRemainingHeight - 0.030449
            },
            {
                id: 22,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length * 2 + width * 2 - 0.0625 - strippingOffset,
                y: topBoxOffsetY - notchHeight + 0.021578
            },
            {
                id: '22a',
                type: 'L',
                relative: false,
                x: glueFlapWidth + length * 2 + width * 2 - 0.0625 - strippingOffset,
                y: topBoxOffsetY + 0.13 + 0.044326 - 0.115824375
            }
        ];

        let bottom = [
            {
                id: 23,
                type: 'V',
                relative: false,
                y: topBoxOffsetY + depth - 0.029891 - 0.0625 + 0.03
            },
            {
                id: 26,
                type: 'l',
                relative: true,
                x:
                    width / 2 +
                    width * 0.318 -
                    width +
                    0.01024 +
                    (-width / 2 + 0.036608 + (-width * 0.318 + 0.069648)), // remove last parentheses if uncommenting prior points
                y: 0 // -width / 2 + 0.028103
            },
            {
                id: 27,
                type: 'c',
                relative: true,
                curve: {
                    type: 'cubic',
                    control_start: {
                        x: -0.015028208333333333,
                        y: -0.015028208333333333
                    },
                    control_end: {
                        x: -0.034274625,
                        y: -0.023301861111111113
                    }
                },
                x: -0.05418565277777778 - 0.009807,
                y: -0.023301861111111113 - 0.009806
            },
            {
                id: 32,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length * 2 + width - width / 2 + 0.025892 - 0.0625,
                y: topBoxOffsetY + depth + width / 2 - 0.066498
            },
            {
                id: 33,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length * 2 + width - length / 2 - 0.0625,
                y: topBoxOffsetY + depth + width / 2 - 0.066498
            },
            {
                id: 37,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length * 2 + width - length + 0.050944,
                y: topBoxOffsetY + depth - 0.143335 + 0.0625
            },
            {
                id: 40,
                type: 'l',
                relative: true,
                x:
                    width / 2 +
                    width * 0.318 -
                    width +
                    0.010234 +
                    (-width / 2 - 0.07683 + (-width * 0.318 + 0.069648)),
                y: 0 //-width / 2 + 0.028117
            },
            {
                id: 41,
                type: 'c',
                relative: true,
                curve: {
                    type: 'cubic',
                    control_start: {
                        x: -0.015028208333333333,
                        y: -0.015041777777777777
                    },
                    control_end: {
                        x: -0.03428141666666667,
                        y: -0.023329000000000003
                    }
                },
                x: -0.054212777777777776 - 0.009786,
                y: -0.023329000000000003 - 0.009793
            },
            {
                id: 42,
                type: 'l',
                relative: true,
                x: -width * 0.1 - 0.065532,
                y: width * 0.1 + 0.064648
            },
            {
                id: 46,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length - width / 2 + 0.025886 - 0.0625,
                y: topBoxOffsetY + depth + width / 2 - 0.066498
            },
            {
                id: 47,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length - length / 2 - 0.0625,
                y: topBoxOffsetY + depth + width / 2 - 0.066498
            },
            {
                id: 51,
                type: 'L',
                relative: false,
                x: glueFlapWidth + length - length + 0.060357,
                y: topBoxOffsetY + depth - 0.032034
            }
        ];

        let path = top;

        if (shapeId === SHAPE_CARTON_AUTOLOCK) {
            path = [...path, ...bottom];
        }

        return path;
    };

    const diffsCollection = () => {
        let diffCoords = [];

        const begin = [
            {id: 0, xDiff: 0.0625, yDiff: 0.048167, total: 0.110667},
            {id: 1, xDiff: 0.008333, yDiff: 0.062611, total: 0.070944},
            {id: 2, xDiff: 0.063333, yDiff: 0.062611, total: 0.125944},
            {id: 3, xDiff: 0.063333, yDiff: 0.025389, total: 0.088722},
            {id: 4, xDiff: 0.056944, yDiff: 0.031778, total: 0.088722},
            {id: 5, xDiff: 0.048709, yDiff: 0.062611, total: 0.111321},
            {id: 6, xDiff: -0.056667, yDiff: 0.062611, total: 0.119278},
            {id: 10, xDiff: 0.063889, yDiff: 0.062611, total: 0.1265},
            {id: 11, xDiff: 0.061667, yDiff: 0.062611, total: 0.124278},
            {id: 12, xDiff: 0.0288, yDiff: 0.056778, total: 0.085578},
            {id: 13, xDiff: -0.011522, yDiff: 0.062333, total: 0.073856},
            {id: 14, xDiff: -0.056889, yDiff: 0.062611, total: 0.1195},
            {id: 15, xDiff: -0.059111, yDiff: 0.062611, total: 0.121722},
            {id: 19, xDiff: 0.061444, yDiff: 0.062611, total: 0.124056},
            {id: 20, xDiff: -0.043932, yDiff: 0.062611, total: 0.106543},
            {id: 21, xDiff: -0.052167, yDiff: 0.031778, total: 0.083944},
            {id: 22, xDiff: -0.059167, yDiff: 0.025389, total: 0.084556},
            {id: 23, xDiff: -0.059167, yDiff: -0.026278, total: 0.085444},
            {id: 24, xDiff: -0.023056, yDiff: -0.062389, total: 0.085444},
            {id: 25, xDiff: 0.045944, yDiff: -0.062389, total: 0.108333},
            {id: 26, xDiff: 0.030556, yDiff: -0.0985, total: 0.129056},
            {id: 28, xDiff: -0.067222, yDiff: -0.020722, total: 0.087944},
            {id: 29, xDiff: -0.061944, yDiff: 0.005667, total: 0.067611},
            {id: 30, xDiff: -0.061944, yDiff: -0.062389, total: 0.124333},
            {id: 31, xDiff: 0.026389, yDiff: -0.062389, total: 0.088778}
        ];

        let mid1 = [
            {id: 32, xDiff: 0.026389, yDiff: -0.062389, total: 0.088778},
            {id: 33, xDiff: -0.0625, yDiff: -0.062389, total: 0.124889}
        ];

        let mid2 = [
            {id: 34, xDiff: -0.0625, yDiff: -0.025583, total: 0.088083},
            {id: 35, xDiff: -0.025694, yDiff: -0.062389, total: 0.088083},
            {id: 36, xDiff: 0.057917, yDiff: -0.062389, total: 0.120306},
            {id: 37, xDiff: 0.051389, yDiff: -0.138778, total: 0.190167},
            {id: 38, xDiff: -0.025, yDiff: -0.062389, total: 0.087389},
            {id: 39, xDiff: 0.044556, yDiff: -0.062389, total: 0.106944},
            {id: 40, xDiff: 0.030556, yDiff: -0.0985, total: 0.129056}, //{"id": 40, "xDiff": 0.030556, "yDiff": -0.098500, "total": 0.129056},
            // {"id": 41, "xDiff": 0.010556, "yDiff": -0.098500, "total": 0.109056},
            {id: 42, xDiff: -0.067222, yDiff: -0.020722, total: 0.087944},
            {id: 43, xDiff: -0.061944, yDiff: 0.005667, total: 0.067611},
            {id: 44, xDiff: -0.061944, yDiff: -0.062389, total: 0.124333},
            {id: 45, xDiff: 0.026389, yDiff: -0.062389, total: 0.088778}
        ];

        let mid3 = [
            {id: 46, xDiff: 0.026389, yDiff: -0.062389, total: 0.088778},
            {id: 47, xDiff: -0.0625, yDiff: -0.062389, total: 0.124889}
        ];

        const end = [
            {id: 48, xDiff: -0.0625, yDiff: -0.025583, total: 0.088083},
            {id: 49, xDiff: -0.025694, yDiff: -0.062389, total: 0.088083},
            {id: 50, xDiff: 0.057917, yDiff: -0.062389, total: 0.120306},
            {id: 51, xDiff: 0.061111, yDiff: -0.027667, total: 0.088778},
            {id: 52, xDiff: 0.063889, yDiff: -0.024889, total: 0.088778}
        ];

        if (length - width < 0.25) {
            mid1 = [];
            mid3 = [];
        }

        diffCoords = [...diffCoords, ...begin, ...mid1, ...mid2, ...mid3, ...end];
        return diffCoords;
    };

    const diffs = diffsCollection();

    let DielineObj = [];
    let safeZone = [];
    let bleedLine = [];

    let absDie;
    let absInner;
    let absOuter;

    // define the paths - non-mirrored
    if (shapeId === SHAPE_CARTON_AUTOLOCK) {
        DielineObj = buildPathFromObj(calculateDieLinesObj(true));
        safeZone = buildPathFromObj(inner());
        bleedLine = buildPathFromObj(
            pathFromDiff(makePathAbsolute(calculateDieLinesObj(false)), diffs)
        );
    } else {
        // mirrored
        // mirrored die line ----------
        const diePath = calculateDieLinesObj(true);
        const absDiePath = makePathAbsolute(diePath);

        DielineObj = buildPathFromObj([
            ...makePathAbsolute(calculateDieLinesObj(true)),
            ...reversePath(
                invertPath(absDiePath, 'y', topBoxOffsetY + bleed + depth - 0.1965 + 0.004)
            )
        ]);

        absDie = [
            ...makePathAbsolute(calculateDieLinesObj(true)),
            ...reversePath(
                invertPath(absDiePath, 'y', topBoxOffsetY + bleed + depth - 0.1965 + 0.004)
            )
        ];

        // mirrored safe zone ----------
        const safePath = inner();
        const absSafePath = makePathAbsolute(safePath);

        safeZone = buildPathFromObj([
            ...makePathAbsolute(inner()),
            ...reversePath(
                invertPath(
                    absSafePath,
                    'y',
                    topBoxOffsetY + depth - 0.26 - paperThickness - bleed * 3 + 0.0133
                )
            ) // TODO make dynamic offset
        ]);

        // mirrored bleed line ----------
        const bleedPath = pathFromDiff(makePathAbsolute(calculateDieLinesObj(false)), diffs);
        const absBleedPath = pathFromDiff(makePathAbsolute(calculateDieLinesObj(false)), diffs); // makePathAbsolute(bleedPath);

        bleedLine = buildPathFromObj([
            ...bleedPath,
            ...reversePath(
                invertPath(
                    absBleedPath,
                    'y',
                    topBoxOffsetY + depth - 0.26 + bleed * 3 - 0.0161 + 0.004 + bleed
                )
            )
        ]);
    }

    const glueKissLines = getGlueKissLines({
        depth,
        topBoxOffsetY: topBoxOffsetY + bleed,
        glueFlapWidth
    });

    const closureKissLines = getClosureKissLines({
        topBoxOffsetY: topBoxOffsetY + bleed,
        leftOffset: 0.5 + length,
        length,
        width,
        depth
    });

    const closureKissLines2 = getClosureKissLines({
        topBoxOffsetY: topBoxOffsetY + bleed,
        leftOffset: 0.5 + length * 2 + width,
        length,
        width,
        depth
    });

    const Crease = () => {
        return (
            <g id="creaseline">
                <path
                    style={creaselineStyle}
                    id="TopTuckCrease"
                    d={[
                        `M ${
                            glueFlapWidth + length + width + tuckFlapFlapCut + tuckFlapInset
                        } ${0.5}`,
                        `l ${length - (tuckFlapFlapCut + tuckFlapInset) * 2} 0`
                    ].join(' ')}
                />
                {shapeId === SHAPE_CARTON_STRAIGHT_TUCK && (
                    <path
                        style={creaselineStyle}
                        id="BottomTuckCrease"
                        d={[
                            `M ${
                                glueFlapWidth + length + width + tuckFlapFlapCut + tuckFlapInset
                            } ${depth + width * 2 + tuckFlap - 0.01 - bleed}`, //- bleed + tuckFlapCutHeight
                            `l ${length - (tuckFlapFlapCut + tuckFlapInset) * 2} 0`
                        ].join(' ')}
                    />
                )}
                {shapeId === SHAPE_CARTON_AUTOLOCK && (
                    <>
                        <path
                            style={creaselineStyle}
                            id="BottomTuckCrease1"
                            d={[
                                `M ${glueFlapWidth + length * 2 + width} ${topBoxOffsetY + depth}`,
                                `l ${-width / 2} ${width / 2}`
                            ].join(' ')}
                        />
                        <path
                            style={creaselineStyle}
                            id="BottomTuckCrease2"
                            d={[
                                `M ${glueFlapWidth + length} ${topBoxOffsetY + depth}`,
                                `l ${-width / 2} ${width / 2}`
                            ].join(' ')}
                        />
                    </>
                )}
                <rect
                    id="artwork_b1"
                    x={glueFlapOffsetX}
                    y={topBoxOffsetY}
                    width={length}
                    height={depth}
                    style={creaselineStyle}
                />
                <rect
                    id="artwork_b2"
                    x={glueFlapOffsetX + length}
                    y={topBoxOffsetY}
                    width={width}
                    height={depth}
                    style={creaselineStyle}
                />
                <rect
                    id="artwork_b3"
                    x={glueFlapOffsetX + length + width}
                    y={topBoxOffsetY - paperThickness}
                    width={length}
                    height={
                        depth + paperThickness + (shapeId === SHAPE_CARTON_STRAIGHT_TUCK ? 0.02 : 0)
                    }
                    style={creaselineStyle}
                />
                <rect
                    id="artwork_b4"
                    x={glueFlapOffsetX + length * 2 + width}
                    y={topBoxOffsetY}
                    width={width - strippingOffset}
                    height={depth}
                    style={creaselineStyle}
                />
            </g>
        );
    };

    if (props.isGettingCreaseDXF) {
        const INCH_TO_MM = 25.4;
        const dxf = new DxfWriter();

        const redLayer = dxf.addLayer('RedLayer', Colors.Red);
        const greenLayer = dxf.addLayer('GreenLayer', Colors.Green);

        const scale = v => v * INCH_TO_MM;

        const lineStartY = 0.125;
        const lineStartX = 0.125;
        const lineSpacing = 0.125;
        let horizontalLineIndex = 0;
        let verticalLineIndex = 0;

        // Tuck Flap Crease Lines
        const tuckFlapCutCount = shapeId === SHAPE_CARTON_STRAIGHT_TUCK ? 2 : 1;
        for (let i = 0; i < tuckFlapCutCount; i++) {
            const posY = lineStartY + lineSpacing * horizontalLineIndex;
            // Rectangle "artwork_b2"
            dxf.addLine(
                point2d(scale(lineStartX), scale(posY)),
                point2d(
                    scale(lineStartX + length - (tuckFlapFlapCut + tuckFlapInset) * 2),
                    scale(posY)
                ),
                {layerName: 'Tuck Flap', colorNumber: Colors.Green}
            );

            horizontalLineIndex++;
        }

        // Horizontal Crease Width Panel Lines
        const shortenedWidthCreaseCount = shapeId === SHAPE_CARTON_STRAIGHT_TUCK ? 4 : 2;
        for (let i = 0; i < shortenedWidthCreaseCount; i++) {
            const posY = lineStartY + lineSpacing * horizontalLineIndex;
            dxf.addLine(
                point2d(scale(lineStartX), scale(posY)),
                point2d(scale(lineStartX + width - notchCutSpace - strippingOffset), scale(posY)),
                {layerName: 'Width Shortened', colorNumber: Colors.Green}
            );

            horizontalLineIndex++;
        }

        if (shapeId === SHAPE_CARTON_AUTOLOCK) {
            // Auto lock will have 2 full size width
            for (let i = 0; i < 2; i++) {
                const posY = lineStartY + lineSpacing * horizontalLineIndex;
                dxf.addLine(
                    point2d(scale(lineStartX), scale(posY)),
                    point2d(scale(lineStartX + width), scale(posY)),
                    {layerName: 'Width', colorNumber: Colors.Green}
                );

                horizontalLineIndex++;
            }
        }

        // Horizontal Crease Length Panel LInes
        const creaseLengthCount = shapeId === SHAPE_CARTON_AUTOLOCK ? 3 : 2;
        for (let i = 0; i < creaseLengthCount; i++) {
            const posY = lineStartY + lineSpacing * horizontalLineIndex;
            dxf.addLine(
                point2d(scale(lineStartX), scale(posY)),
                point2d(scale(lineStartX + length), scale(posY)),
                {layerName: 'Length', colorNumber: Colors.Green}
            );
            horizontalLineIndex++;
        }

        // Vertical Crease Panel Lines
        for (let i = 0; i < 4; i++) {
            const posY = lineStartY + lineSpacing * horizontalLineIndex;
            const posX = lineStartX + lineSpacing * verticalLineIndex;
            dxf.addLine(
                point2d(scale(posX), scale(posY)),
                point2d(scale(posX), scale(posY + depth)),
                {layerName: 'Depth', colorNumber: Colors.Green}
            );

            verticalLineIndex++;
        }

        // Additional lines for SHAPE_CARTON_AUTOLOCK
        if (shapeId === SHAPE_CARTON_AUTOLOCK) {
            for (let i = 0; i < 2; i++) {
                const posX = lineStartX + lineSpacing * verticalLineIndex;
                const posY = lineStartY + lineSpacing * horizontalLineIndex;

                // The part of the line that overlaps the closure panel
                const overlap = width * 0.1;

                dxf.addLine(
                    point2d(scale(posX), scale(posY)),
                    point2d(scale(posX + width / 2 - overlap), scale(posY + width / 2 - overlap)),
                    {
                        layerName: 'Lock Bottom',
                        colorNumber: Colors.Green
                    }
                );

                verticalLineIndex++;
            }
        }

        // Outer Rectangle - Red, Closed Path
        const outerVertices = [
            {point: point2d(scale(0), scale(0))},
            {
                point: point2d(scale(0), scale((horizontalLineIndex + 2) * lineSpacing + depth))
            },
            {
                point: point2d(
                    scale(Math.max(width, length) + lineSpacing * 2),
                    scale((horizontalLineIndex + 2) * lineSpacing + depth)
                )
            },
            {point: point2d(scale(Math.max(width, length) + lineSpacing * 2), scale(0))}
        ];
        dxf.addLWPolyline(outerVertices, {
            layerName: 'Border',
            flags: 1,
            colorNumber: Colors.Red
        });

        return dxf.stringify();
    }

    function isLayerVisible(layer) {
        return props.visibleLayers?.indexOf(layer) > -1;
    }

    const isLayoutOnly = props.isLayoutOnly;

    return (
        <ViewBox
            x={0}
            y={0}
            width={flatWidth}
            length={flatHeight}
            useViewBox={props.useViewBox ?? true}>
            {isLayerVisible('kisscutline') && !isLayoutOnly && (
                <g id="Kiss Cut">
                    {glueKissLines.map((l, index) => {
                        return (
                            <path
                                key={index}
                                style={kissStyle}
                                id={`kiss${index}`}
                                d={l.join(' ')}
                            />
                        );
                    })}
                    {shapeId === SHAPE_CARTON_AUTOLOCK &&
                        closureKissLines.map((l, index) => {
                            return (
                                <path
                                    key={index}
                                    style={kissStyle}
                                    id={`closure${index}`}
                                    d={l.join(' ')}
                                />
                            );
                        })}
                    {shapeId === SHAPE_CARTON_AUTOLOCK &&
                        closureKissLines2.map((l, index) => {
                            return (
                                <path
                                    key={index}
                                    style={kissStyle}
                                    id={`closure${index}`}
                                    d={l.join(' ')}
                                />
                            );
                        })}
                </g>
            )}
            {isLayerVisible('knockout') && !isLayoutOnly && (
                <g id="knockouts-duplex">
                    <path
                        style={knockoutStyle}
                        id="GlueFlapKnockoutDuplex"
                        d={buildPathFromObj(
                            getGlueFlapKnockoutDuplex({
                                offsetLeft: length * 2 + width * 2 + 0.125 - strippingOffset,
                                glueFlapWidth,
                                depth,
                                topBoxOffsetY
                            })
                        )}
                    />
                    {shapeId === SHAPE_CARTON_AUTOLOCK && (
                        <>
                            <path
                                style={knockoutStyle}
                                id="BottomTuckKnockoutDuplex"
                                d={buildPathFromObj(
                                    getBottomTuckKnockoutDuplex({
                                        offsetLeft: glueFlapWidth + length,
                                        topBoxOffsetY,
                                        depth,
                                        width,
                                        length,
                                        bottomTuckHeight,
                                        bleed
                                    })
                                )}
                            />
                            <path
                                style={knockoutStyle}
                                id="BottomTuckKnockoutRightDuplex"
                                d={buildPathFromObj(
                                    getBottomTuckKnockoutDuplex({
                                        offsetLeft: glueFlapWidth + length * 2 + width,
                                        topBoxOffsetY,
                                        depth,
                                        width,
                                        length,
                                        bottomTuckHeight,
                                        bleed
                                    })
                                )}
                            />
                        </>
                    )}
                </g>
            )}
            {isLayerVisible('knockout') && !isLayoutOnly && (
                <g id="knockouts">
                    <path
                        style={knockoutStyle}
                        id="GlueFlapKnockout"
                        d={buildPathFromObj(
                            getGlueFlapKnockout({
                                offsetLeft: bleed * -1,
                                depth,
                                topBoxOffsetY,
                                glueFlapWidth
                            })
                        )}
                    />

                    {shapeId === SHAPE_CARTON_AUTOLOCK && !isLayoutOnly && (
                        <>
                            <path
                                style={knockoutStyle}
                                id="TuckFlapKnockoutLeft"
                                d={buildPathFromObj(
                                    getTuckFlapKnockout({
                                        offsetLeft: glueFlapWidth + length,
                                        bottomTuckHeight,
                                        width,
                                        length,
                                        depth,
                                        topBoxOffsetY
                                    })
                                )}
                            />
                            <path
                                style={knockoutStyle}
                                id="TuckFlapKnockoutRight"
                                d={buildPathFromObj(
                                    getTuckFlapKnockout({
                                        offsetLeft: glueFlapWidth + length * 2 + width,
                                        bottomTuckHeight,
                                        width,
                                        length,
                                        depth,
                                        topBoxOffsetY
                                    })
                                )}
                            />
                        </>
                    )}
                </g>
            )}
            {isLayerVisible('creaseline') && <Crease />}
            {isLayerVisible('bleedline') && !isLayoutOnly && (
                <>
                    <path id="bleedline" d={bleedLine} style={bleedlineStyle} />
                </>
            )}
            {isLayerVisible('dieline') && (
                <>
                    <path id="dieline" d={DielineObj} style={dielineStyle} />
                </>
            )}
            {isLayerVisible('safezone') && !isLayoutOnly && (
                <>
                    <path id="safeZone" d={safeZone} style={safeZoneStyle} />
                </>
            )}
        </ViewBox>
    );
}
